import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchProjects, deleteProject } from '../services/projectService';
import { Button, Loader } from '../components/common';
import { getImageUrl } from '../utils/imageUrl';
import AdminLayout from './AdminLayout';

const ProjectList = () => {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadProjects = async () => {
        setLoading(true);
        try {
            const { data } = await fetchProjects();
            setProjects(data);
        } catch (error) {
            console.error('Failed to load projects', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProjects();
    }, []);

    const deleteHandler = async (id) => {
        if (window.confirm('Are you sure you want to delete this project?')) {
            try {
                await deleteProject(id);
                loadProjects();
            } catch (error) {
                console.error('Failed to delete project', error);
            }
        }
    };

    return (
        <AdminLayout title="Projects">
            <div className="sm:flex sm:items-center sm:justify-between mb-8">
                <div>
                    <h1 className="text-xl font-bold text-gray-200">Portfolio Projects</h1>
                    <p className="mt-2 text-sm text-gray-400">Manage all portfolio projects, categories, screenshots, and details.</p>
                </div>
                <div className="mt-4 sm:mt-0">
                    <Link to="/admin/projects/add">
                        <Button variant="primary" icon={<i className="fas fa-plus"></i>}>
                            Add Project
                        </Button>
                    </Link>
                </div>
            </div>

            <div className="flex flex-col">
                <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg bg-gray-800 border border-gray-700">
                            <table className="min-w-full divide-y divide-gray-700">
                                <thead className="bg-gray-900/80 backdrop-blur-sm sticky top-0 z-10">
                                    <tr>
                                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-bold text-gray-400 uppercase tracking-wider sm:pl-6">Project</th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Category</th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Client</th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-xs font-bold text-gray-400 uppercase tracking-wider">Tech Stack</th>
                                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Actions</span></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-700 bg-gray-800">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="5" className="py-12 text-center text-gray-400">
                                                <Loader size="md" text="Loading projects..." />
                                            </td>
                                        </tr>
                                    ) : projects.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="py-12 text-center text-gray-500">
                                                <i className="fas fa-folder-open text-3xl mb-2 opacity-30 d-block"></i>
                                                No projects found. Click "Add Project" to create one.
                                            </td>
                                        </tr>
                                    ) : (
                                        projects.map((project) => (
                                            <tr key={project._id} className="hover:bg-gray-700/50 transition-colors group">
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                                                    <div className="flex items-center">
                                                        <div className="h-10 w-10 flex-shrink-0">
                                                            <img
                                                                className="h-10 w-10 rounded-lg object-cover ring-1 ring-gray-600 group-hover:ring-indigo-500 transition-all"
                                                                src={getImageUrl(project.img)}
                                                                alt={project.title}
                                                            />
                                                        </div>
                                                        <div className="ml-4">
                                                            <div className="font-semibold text-white group-hover:text-indigo-400 transition-colors">{project.title}</div>
                                                            <div className="text-xs text-gray-400 truncate max-w-xs">{project.subTitle || project.description}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                                                    <span className="inline-flex items-center rounded-full bg-indigo-900/50 px-2.5 py-0.5 text-xs font-medium text-indigo-300 border border-indigo-700/50">
                                                        {project.category || 'Engineering'}
                                                    </span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">{project.client || 'N/A'}</td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-300">
                                                    <div className="flex flex-wrap gap-1 max-w-xs">
                                                        {project.technologies?.slice(0, 3).map((tech, idx) => (
                                                            <span key={idx} className="bg-gray-700 text-gray-300 text-xs px-2 py-0.5 rounded">
                                                                {tech}
                                                            </span>
                                                        ))}
                                                        {project.technologies?.length > 3 && (
                                                            <span className="text-xs text-gray-500">+{project.technologies.length - 3}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                                    <div className="flex items-center justify-end space-x-2">
                                                        <Link
                                                            to={`/admin/projects/edit/${project._id}`}
                                                            className="text-indigo-400 hover:text-indigo-300 bg-gray-700/50 p-2 rounded-md hover:bg-gray-600 transition-all"
                                                            title="Edit Project"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                        </Link>
                                                        <button
                                                            onClick={() => deleteHandler(project._id)}
                                                            className="text-red-400 hover:text-red-300 bg-gray-700/50 p-2 rounded-md hover:bg-gray-600 transition-all"
                                                            title="Delete Project"
                                                        >
                                                            <i className="fas fa-trash-alt"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
};

export default ProjectList;
